const getApiBaseUrl = (): string => {
    // This will use the .env variable in production, and the proxy in development.
    return process.env.REACT_APP_API_URL || '';
};

const getAuthHeader = () => {
    const idToken = sessionStorage.getItem('idToken');
    return {
        'Authorization': `Bearer ${idToken || ''}`,
        'Content-Type': 'application/json'
    };
};

let isRefreshing = false;
let failedQueue: { resolve: (value?: any) => void; reject: (reason?: any) => void; }[] = [];

const processQueue = (error: Error | null, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const refreshToken = async (): Promise<string> => {
    isRefreshing = true;
    const refreshTokenValue = localStorage.getItem('refreshToken');
    if (!refreshTokenValue) {
        isRefreshing = false;
        return Promise.reject(new Error('No refresh token available'));
    }

    try {
        const response = await fetch(`${getApiBaseUrl()}/api/pub/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: refreshTokenValue }),
        });

        if (!response.ok) {
            throw new Error('Session refresh failed');
        }

        const newTokens = await response.json();
        sessionStorage.setItem('idToken', newTokens.idToken);
        if (newTokens.refreshToken) {
            localStorage.setItem('refreshToken', newTokens.refreshToken);
        }
        processQueue(null, newTokens.idToken);
        return newTokens.idToken;
    } catch (error) {
        processQueue(error as Error, null);
        // If refresh fails, dispatch a global event to trigger logout
        window.dispatchEvent(new Event('auth-error'));
        return Promise.reject(error);
    } finally {
        isRefreshing = false;
    }
};

export const apiFetch = async (path: string, options: RequestInit = {}): Promise<Response> => {
    const makeRequest = async (): Promise<Response> => {
        const baseUrl = getApiBaseUrl();
        const url = `${baseUrl}${path}`;
        const headers = { ...getAuthHeader(), ...options.headers };

        if (!sessionStorage.getItem('idToken') && (headers as any)['Authorization']) {
            delete (headers as any)['Authorization'];
        }

        return fetch(url, { ...options, headers });
    };

    let response = await makeRequest();

    if (response.status === 401) {
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(() => makeRequest());
        }

        try {
            await refreshToken();
            response = await makeRequest();
        } catch (error) {
            // The refreshToken function will dispatch the auth-error event.
            // We just need to reject the promise for the original apiFetch call.
            return Promise.reject(error);
        }
    }

    return response;
};
